import React from "react";
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { C } from "../../../common/theme";
import { TrafficDay } from "../../../common/requests/platform/endpoints";
import { EmptyState, Panel, chartSx } from "../../../ui";
import { labelStride } from "../../Sales/salesData";
import { SERIES_COLOR, formatCompact, formatCount, formatDayLong, formatDayShort, isToday, useWidth } from "../platformData";

type Metric = "views" | "sessions";

const METRICS: { key: Metric; label: string; noun: string }[] = [
  { key: "views", label: "Page views", noun: "page views" },
  { key: "sessions", label: "Sessions", noun: "sessions" },
];
const HEIGHT = 280;
const MARGIN = { left: 48, right: 8, top: 12, bottom: 28 };

interface TrafficChartProps {
  daily: TrafficDay[];
  /** Period totals from the summary; daily sessions don't add up to them (a session can span days). */
  totals: { views: number; sessions: number };
}

export default function TrafficChart({ daily, totals }: TrafficChartProps) {
  const [metric, setMetric] = React.useState<Metric>("views");
  const meta = METRICS.find((m) => m.key === metric)!;
  const keys = daily.map((d) => d.date);
  const values = daily.map((d) => d[metric]);
  const todayIndex = keys.findIndex((k) => isToday(k));
  const hasPartial = todayIndex !== -1;
  const [plotRef, width] = useWidth<HTMLDivElement>();
  const stride = labelStride(keys.length, width - MARGIN.left - MARGIN.right, "day");

  // Keys (not labels) are the categories; the formatter turns them into "4 Oct" ticks and full tooltip dates.
  const xAxisCommon = {
    data: keys,
    valueFormatter: (key: string, ctx: { location: string }) =>
      ctx.location === "tick" ? formatDayShort(key) : `${formatDayLong(key)}${isToday(key) ? " (so far)" : ""}`,
    // Ticks (and so labels) on evenly spaced days; a tick per day is a solid comb over a year.
    tickInterval: (_: unknown, index: number) => index % stride === 0,
  };
  const yAxis = [{ valueFormatter: (v: number | null) => formatCompact(v), tickMinStep: 1 }];
  const series = [{ data: values, label: meta.label, color: SERIES_COLOR, valueFormatter: (v: number | null) => (v === null ? "–" : formatCount(v)) }];

  return (
    <Panel padding={1.5} sx={{ height: "100%", boxSizing: "border-box" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box sx={{ mr: "auto", minWidth: 0 }}>
          <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700 }}>
            {meta.label} by day
          </Typography>
          <Typography variant="caption" sx={{ color: C.muted }}>
            {formatCount(totals[metric])} {meta.noun} in this period
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)} aria-label="Measure">
          {METRICS.map((m) => (
            <ToggleButton key={m.key} value={m.key} sx={{ py: 0.5, px: 1.25, minHeight: 36, textTransform: "none" }}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box ref={plotRef} sx={{ minWidth: 0 }}>
        {totals.views === 0 ? (
          <Box sx={{ height: HEIGHT, display: "grid", placeItems: "center" }}>
            <EmptyState title="No page views in this period." />
          </Box>
        ) : (
          <LineChart
            height={HEIGHT}
            skipAnimation
            slotProps={{ legend: { hidden: true } }}
            grid={{ horizontal: true }}
            margin={MARGIN}
            xAxis={[{ scaleType: "point", ...xAxisCommon }]}
            yAxis={yAxis}
            series={series.map((s) => ({ ...s, area: true, showMark: false }))}
            sx={{ ...chartSx, "& .MuiAreaElement-root": { fillOpacity: 0.08 }, "& .MuiLineElement-root": { strokeWidth: 2 } }}
          />
        )}
      </Box>
      {hasPartial && totals.views > 0 && (
        <Typography variant="caption" sx={{ color: C.muted }}>
          The last point is today, still in progress.
        </Typography>
      )}
    </Panel>
  );
}
